import React, { useState, useEffect, useMemo } from 'react';
import { Clock, MessageSquare, TrendingUp, Flag, ChevronDown, Loader } from 'lucide-react';
import { ChatIcon, JoinRacesIcon, MarketIcon, MovingDown, MovingNone, MovingUp, OpenInNew } from '../../../Icons';
import CryptoIconWrapper from '../../../cryptoicons/CryptoIconWrapper';
import NewsApi from '../../../../utils/NewsApi';
import { useAccount } from 'wagmi';

const CryptoUpdates = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [comment, setComment] = useState('');
  const [visibleItems, setVisibleItems] = useState(3);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [updates, setUpdates] = useState([]);
  const [newsUpdates, setNewsUpdates] = useState([]);
  const [chatUpdates, setChatUpdates] = useState([]);
  const { isConnected, address } = useAccount();

  // Define tabs configuration
  const tabs = [
    {
      id: 'all',
      label: 'All',
      icon: null,
    },
    {
      id: 'market',
      label: 'Market Updates',
      icon: MarketIcon,
    },
    {
      id: 'chat',
      label: 'Chat',
      icon: ChatIcon,
    },
  ];

  // Function to format timestamps
  const formatTimeAgo = timestamp => {
    // Convert Unix timestamp (seconds) to milliseconds if needed
    const timestampMs = timestamp > 10000000000 ? timestamp : timestamp * 1000;

    const now = Date.now();
    const diffInSeconds = Math.floor((now - timestampMs) / 1000);

    if (diffInSeconds < 5) {
      return 'Just now';
    } else if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
  };

  const generateMarketData = data => {
    const result = [];

    for (let i = 0; i < data.length; i++) {
      const categories = data[i].CATEGORY_DATA.slice(0, 3)
        .map(item => item.CATEGORY)
        .join(' | ');

      result.push({
        id: data[i].ID,
        type: 'market',
        categories,
        message: data[i].TITLE,
        timestamp: data[i].CREATED_ON,
        sentiment: data[i].SENTIMENT,
        sourceName: data[i].SOURCE_DATA.NAME,
        sourceUrl: data[i].URL,
      });
    }

    return result;
  };

  const fetchUpdates = async () => {
    setIsLoading(true);

    // Simulate API fetch delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    let fetchedUpdates;
    switch (activeTab) {
      case 'market':
        fetchedUpdates = marketUpdates;
        break;
      case 'chat':
        fetchedUpdates = chatMessages;
        break;
      default: // 'all'
        fetchedUpdates = [...marketUpdates, ...chatMessages].sort((a, b) => {
          // Sort by timestamp (newest first)
          return b.timestamp - a.timestamp;
        });
        break;
    }

    setUpdates(fetchedUpdates);
    setIsLoading(false);
    setIsExpanded(false);
  };

  const fetchData = async () => {
    try {
      const result = await NewsApi.getNewsData({ categories: ['BTC'] });
      // Do something with result, like setting state
      const generatedData = generateMarketData(result.Data);
      setNewsUpdates(generatedData);
      setChatUpdates([
        {
          id: 7,
          type: 'chat',
          username: '0x3991...2d4e',
          message: "Let's go BTC!",
          timestamp: now,
        },
        {
          id: 8,
          type: 'chat',
          username: '0x7f42...9e3a',
          message: 'ETH is the future!',
          timestamp: sevenMinutesAgo,
        },
      ]);
      fetchUpdates();
    } catch (error) {
      console.error('Error fetching news data:', error);
      // Handle error appropriately
    }
  };

  // Fetch updates based on active tab
  useEffect(() => {
    fetchUpdates();
  }, [activeTab, chatUpdates, newsUpdates]);

  useEffect(() => {
    fetchData();
  }, []);

  const marketUpdates = useMemo(() => {
    return newsUpdates;
  }, [newsUpdates]);

  const chatMessages = useMemo(() => {
    return chatUpdates;
  }, [chatUpdates]);
  // Calculate timestamps based on current time
  const now = Date.now();
  const sevenMinutesAgo = now - 7 * 60 * 1000;

  // State to store formatted times
  const [formattedTimes, setFormattedTimes] = useState({});

  // Display updates based on visibleItems and expanded state
  const displayUpdates = () => {
    return isExpanded ? updates : updates.slice(0, visibleItems);
  };

  // Handle "Show more" button click
  const handleShowMore = () => {
    if (isExpanded) {
      setIsExpanded(false);
      // Scroll back to top of component when collapsing
      window.scrollTo({ top: document.getElementById('crypto-updates').offsetTop, behavior: 'smooth' });
    } else {
      setIsExpanded(true);
    }
  };

  // Handle post comment
  const handlePostComment = async () => {
    if (comment.trim()) {
      const chatData = {
        id: [...marketUpdates, ...chatMessages].length + 1,
        type: 'chat',
        username: address,
        message: comment,
        timestamp: now,
      };

      await setChatUpdates([chatData, ...chatUpdates]);

      setComment('');
    }
  };

  const renderIconType = type => {
    if (type === 'market') {
      return <MarketIcon className="h-8 w-8 fill-[#9937F7]" />;
    } else if (type === 'chat') {
      return <ChatIcon className="h-8 w-8 fill-[#B5B5F8]" />;
    } else {
      return <JoinRacesIcon className="h-8 w-8 fill-white" />;
    }
  };

  const renderIconSentiment = sentiment => {
    if (sentiment === 'POSITIVE') {
      return <MovingUp className="h-5 w-5" />;
    } else if (sentiment === 'NEGATIVE') {
      return <MovingDown className="h-5 w-5" />;
    } else {
      return <MovingNone className="h-5 w-5" />;
    }
  };

  // Function to render tab buttons dynamically
  const renderTabButtons = () => {
    return tabs.map(tab => (
      <button
        key={tab.id}
        className={`rounded-full px-4 py-2 ${
          tab.icon ? 'flex items-center space-x-2' : ''
        } group font-medium hover:border-white hover:text-white ${
          activeTab === tab.id
            ? 'bg-white !text-black'
            : 'border-thin border-solid border-gray-2 bg-transparent text-gray-2'
        } ${tab.id === 'all' ? 'text-white' : ''}`}
        onClick={() => setActiveTab(tab.id)}
        disabled={isLoading}
      >
        {tab.icon ? (
          <span className="flex items-center">
            {React.createElement(tab.icon, {
              className: `w-5 h-5 ${activeTab === tab.id ? 'fill-black' : 'fill-gray-2 group-hover:fill-white'}`,
            })}
            <span className="ml-1 md:block">{tab.label}</span>
          </span>
        ) : (
          tab.label
        )}
      </button>
    ));
  };

  // Loading skeleton for updates
  const renderLoadingSkeleton = () => {
    return Array(3)
      .fill(0)
      .map((_, index) => (
        <div key={`skeleton-${index}`} className="animate-pulse border-b border-gray-800 py-4">
          <div className="flex items-start space-x-6">
            {/* Icon skeleton */}
            <div className="h-10 w-10 rounded-xl bg-gray-800"></div>

            {/* Content skeleton */}
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <div className="h-6 w-6 rounded-full bg-gray-800"></div>
                <div className="h-4 w-20 rounded bg-gray-800"></div>
              </div>

              <div className="my-2 h-4 w-3/4 rounded bg-gray-800"></div>

              <div className="flex items-center space-x-3">
                <div className="h-6 w-16 rounded bg-gray-800"></div>
                <div className="h-4 w-32 rounded bg-gray-800"></div>
              </div>
            </div>

            {/* Timestamp skeleton */}
            <div className="h-4 w-24 self-center rounded bg-gray-800"></div>
          </div>
        </div>
      ));
  };

  return (
    <div
      id="crypto-updates"
      className="mx-auto w-full overflow-hidden rounded-3xl border border-solid border-[#282133] bg-black p-6 text-white"
    >
      <h2 className="mb-4 text-2xl font-bold">Play by Play</h2>

      {/* Dynamically rendered tabs */}
      <div className="mb-6 flex space-x-2 font-medium">{renderTabButtons()}</div>

      {/* Comment input - only visible for 'all' or 'chat' tabs */}
      {isConnected && (activeTab === 'all' || activeTab === 'chat') && (
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="Write a comment..."
            className="text-md w-full rounded-2xl border-[1px] border-solid border-[rgba(40,33,51,1)] bg-[rgba(255,255,255,0.05)] bg-white/5 px-4 py-4 leading-5 text-white placeholder-gray-1 focus:outline-none"
            value={comment}
            onChange={e => setComment(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                // Your function to execute when Enter is pressed
                handlePostComment();
                // Optionally prevent default behavior (like form submission)
                e.preventDefault();
              }
            }}
            disabled={isLoading}
            style={{
              boxShadow: '0 0 0 1px rgba(86, 17, 152, 0.5)',
              outline: 'rgba(86, 17, 152, 0)',
            }}
          />
          <button
            className={`absolute right-2 top-1/2 -translate-y-1/2 transform rounded-lg bg-white px-4 py-2 text-sm font-bold leading-4 text-black ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={handlePostComment}
            disabled={isLoading}
          >
            Post
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {isLoading ? (
        <div className="space-y-4">
          {/* Loading indicator at the top */}
          <div className="flex items-center justify-center py-2 text-purple-500">
            <Loader className="mr-2 h-6 w-6 animate-spin" />
            <span>Loading updates...</span>
          </div>

          {/* Loading skeleton UI */}
          {renderLoadingSkeleton()}
        </div>
      ) : (
        /* Updates Feed */
        <div className="space-y-0">
          {displayUpdates().map(update => (
            <div key={update.id} className="border-b border-gray-800 py-4">
              <div className="flex items-start space-x-6">
                {/* Icon */}
                <div className="h-10 w-10 rounded-xl bg-[#282133] p-1">{renderIconType(update.type)}</div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    {update.type === 'race' && <CryptoIconWrapper name={update.coin.toLowerCase()} size={24} />}
                    {update.type === 'chat' && <div className="h-6 w-6 rounded-full bg-purple-500"></div>}
                    {update.type === 'market' && <span className="text-gray-1">{update.categories}</span>}
                    {/* {update.type === 'market' && <CryptoIconWrapper name={'btc'} size={24} />} */}
                    {(update.type === 'race' || update.type === 'chat') && (
                      <span className="text-sm">
                        {update.coin ||
                          `${update.username.substring(0, 6)}...${update.username.substring(update.username.length - 4)}`}
                      </span>
                    )}
                    {update.coinFullName && <span className="text-xs text-gray-1">{update.coinFullName}</span>}

                    {update.sentiment && (
                      <span className="text-sm text-gray-1">{renderIconSentiment(update.sentiment)}</span>
                    )}
                    {update.sourceName && <span className="text-sm text-gray-1">{update.sourceName}</span>}
                  </div>

                  <p className={`my-1 text-white ${update.type === 'chat' ? 'w-fit rounded-lg bg-white/10 p-2' : ''}`}>
                    {update.message}
                  </p>

                  {update.type !== 'chat' ? (
                    <div className="flex items-center space-x-3">
                      {update?.position && (
                        <span
                          className={`inline-block rounded bg-opacity-15 px-2 py-1 text-xs ${update.change.startsWith('-') ? 'bg-[#FF3B30] text-[#FF3B30]' : 'bg-[#10C083] text-[#10C083]'}`}
                        >
                          {update.change}
                        </span>
                      )}
                      {update?.position && (
                        <span className="text-sm text-gray-1">
                          Position: {update.position.from} → {update.position.to}
                        </span>
                      )}

                      {update?.sourceUrl && (
                        <div
                          className="flex cursor-pointer items-center font-bold"
                          onClick={() => window.open(update.sourceUrl, '_blank', 'noopener,noreferrer')}
                        >
                          <OpenInNew className="mr-1 h-5 w-5" />
                          <span className="text-sm text-[#007AFF]">Read more</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Timestamp */}
                <div className="flex items-center self-center text-sm text-gray-500">
                  <Clock size={14} className="mr-1" />
                  <span>{formattedTimes[update.id] || formatTimeAgo(update.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show more/less button - only show when not loading and there are more items to show */}
      {!isLoading && updates.length > visibleItems && (
        <div className="mt-4 flex justify-center">
          <button
            className="flex items-center space-x-1 rounded-full border border-gray-700 bg-transparent px-6 py-2 text-white transition-colors hover:bg-gray-800"
            onClick={handleShowMore}
          >
            <span>{isExpanded ? 'Show less' : 'Show more'}</span>
            <ChevronDown size={16} className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CryptoUpdates;
